import { RouteObject, useNavigate, useRoutes, Navigate, useLocation } from "react-router-dom";
import { App } from "antd";
import { observer } from "mobx-react-lite";
import { useStore } from "../stores/root-store-context";
import { useEffect, useState } from "react";
import Home from "../pages/Home";
import Login from "../pages/Login";
import Account from "../pages/Users/UserPage";
import RequireAuth from "../providers/RequireAuth";
import CompanyHome from "../pages/CompanyHome";
import Users from "../pages/Users/UsersList";
import Error404 from "../pages/Error404";
import Projects from "../pages/Projects/ProjectsList";
import Tests from "../pages/Tests/Tests";
import Register from "../pages/Register";
import About1 from "../components/sections/About1";
import About from "../pages/About/About";
import Preloader from "../components/elements/Preloader";
import EditEmployee from "../pages/Users/EditEmployee";
import ProjectsList from "../pages/Projects/ProjectsList";
import ProjectPage from "../pages/Projects/ProjectPage";
import TaskPage from "../pages/ProjectTasks/ProjectTaskPage";
import ProjectSettings from "../pages/Projects/ProjectSettings";
import TimeReports from "../pages/TimeReports/TimeReports";
import EventPage from "../pages/Events/EventPage";
import PaymentAndTariffs from "../pages/Payment/PaymentAndTariffs"
import dayjs from "dayjs";
import "dayjs/locale/uk";
import "dayjs/locale/en";
import "dayjs/locale/ru";
import i18n from "../i18n";
import SalariesPage from "../pages/Salaries/SalariesPage";
import OrganogramPage from "../pages/Users/OrganogramPage";
import CompanySettings from "../pages/CompanySettings";
import CheckCompanyStatus from "./CheckCompanyStatus";
import PrivacyPolicy from "../pages/PrivacyPolicy/PrivacyPolicy";
import RegisterByInvitation from "../pages/RegisterByInvitation";
import SupportPage from "../pages/Support/SupportPage";
import EditOrAddTest from "../pages/Tests/EditOrAddTest";
import PassageTest from "../pages/Tests/PassageTest";
import Resource from "../pages/Users/Resource";
import { UserAnswersTest } from "../pages/Tests/UserAnswersTest";
import AboutCompany from "../pages/About/AboutCompany";
import Layout from "../components/layout/Layout";
import { Tariffs } from "../components/sections/Tariffs";
import IntegrationAPI from "../pages/API/IntegrationAPI";
import Kanban from "../pages/Projects/Kanban";
import RequireCompanyTariff from "../providers/RequireCompanyTariff";
import PublicOfferEn from "../pages/PublicOffers/PublicOfferEn";
import PublicOfferRu from "../pages/PublicOffers/PublicOfferRu";
import PublicOfferUk from "../pages/PublicOffers/PublicOfferUk";
import KanbanTasks from "../pages/KanbanTasks/KanbanTasks";
import ProjectContainerLayout from "../pages/Projects/ProjectContainerLayout";
import ProjectFiles from "../pages/Projects/ProjectFiles";
import CompanyFinances from "../pages/ERP/Finances/CompanyFinances";
import CompanyCRM from "../pages/CRM/CompanyCRM";
import EventDetail from "../pages/CRM/EventDetail";
import ExternalCompanyDetail from "../pages/CRM/ExternalCompanyDetail";
import ContactDetail from "../pages/CRM/ContactDetail";
import DeleteAccount from "../pages/Users/DeleteAccount";
import EnhancedCalendar from "../pages/Extended Calendar/EnhancedCalendar";
import TicketSupportPage from "../pages/Support/TicketSupportPage";
import SupportSystem from "../pages/Support/SupportSystem";
import CrmCalendar from "../pages/CRM/CrmCalendar";
import ForgotPasswordForm from "../pages/Login/ForgotPassword";
import ResetPasswordForm from "../pages/Login/RestorePassword";
import AnonymousPage from "../pages/Anonymous/AnonymousPage";
import LinkExpired from "../pages/Anonymous/LinkExpired";
import ConfirmEmail from "../pages/Login/ConfirmEmail";
import ConfirmChangedEmail from "../pages/Login/ConfirmChangedEmail";
import { Roles } from "../enums/Roles";
import { TypePage } from "../enums/TypePage";
import { TariffPlan } from "../enums/TariffPlan";


const Router: React.FC = observer(() => {
  const rootStore = useStore();
  const { apiStore, applicationStore, employeesStore, projectsStore } = rootStore;
  const [routings, setRoutings] = useState<RouteObject[]>([]);
  const navigate = useNavigate();
  const [isProjectLoaded, setIsProjectLoaded] = useState(false);

  dayjs.locale(i18n.language);

  const { notification } = App.useApp();

  const location = useLocation();
  
  useEffect(() => {
    if (location.pathname.includes("/projects/")) {
      setIsProjectLoaded(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    let token = localStorage.getItem("token");
    apiStore.setConfigApi({
      token: localStorage.getItem("token") as string,
      companyId: "0",
      userId: 0,
    });
    applicationStore.getConfig().then((res) => {
      if (res && typeof res === 'object' && 'isShowLanguage' in res) {
        applicationStore.setLocalConfig(res);
        const lang = res.defaultLanguage;
        const showLanguage = res.isShowLanguage;
        const domain = res.apiDomain;
        const saveLng = localStorage.getItem('i18nextLng');
        if (saveLng) {
          i18n.changeLanguage(saveLng);
        } else {
          i18n.changeLanguage(lang);
        }
        localStorage.setItem("showLanguage", showLanguage.toString());
        localStorage.setItem("domain", domain);
      } else {
        const browsLang = window.navigator.language;
        let lang = "en";
        switch (browsLang) {
          case "ru-RU":
            lang = "uk";
            break;
          case "uk-UA":
            lang = "uk";
            break;
          case 'en-US':
          case 'en':
            lang = "en";
            break;
          default:
            lang = "en";
            break;
        }
        i18n.changeLanguage(lang);
        localStorage.setItem("showLanguage", true.toString());
      }
    }).finally(() => {
      if (token) {
        applicationStore.setLoading(true);
        applicationStore.autorize();
        employeesStore.getUsers();
        projectsStore.getAll();
      } else {
        applicationStore.setIsAuthenticated(false);
        applicationStore.setLoading(false);
      }
    });

  }, []);

  useEffect(() => {
    let token = localStorage.getItem("token");
    if (!token && applicationStore.isAuthenticated) {
      applicationStore.setIsAuthenticated(false);
      console.log("navigate(/login);");
      navigate("/login");
    }
    else {
      apiStore.setConfigApi({
        token: localStorage.getItem("token") as string,
        companyId: "0",
        userId: 0
      });
      // employeesStore.getUsers();
      // projectsStore.getAll();
    }

  }, [applicationStore.isAuthenticated]);

  const routes = [
    {
      path: "/",
      element: (
        <CheckCompanyStatus>
          {applicationStore.isAuthenticated ? <CompanyHome /> : <Home />}
        </CheckCompanyStatus>
      ),
    },
    { path: "/:companyName/RegisterByInvitation", element: <RegisterByInvitation /> },
    { path: "/about", element: <About /> },
    { path: "/forgot-password", element: <ForgotPasswordForm /> },
    { path: "/reset-password", element: <ResetPasswordForm /> },
    { path: "/AboutCompany", element: <AboutCompany /> },
    { path: "/en/public-offer", element: <PublicOfferEn /> },
    { path: "/ru/public-offer", element: <PublicOfferRu /> },
    { path: "/uk/public-offer", element: <PublicOfferUk /> },
    { path: "/privacy-policy", element: <PrivacyPolicy /> },
    { path: "/link-expired", element: <LinkExpired /> },
    {
      path: "/prices",
      element: !applicationStore.isAuthenticated ?
        <Layout headerStyle={1} footerStyle={2}>
          <div style={{ margin: '50px auto', maxWidth: '1300px' }}>
            <Tariffs />
          </div>
        </Layout> : <Navigate to="/" replace />
    },
    {
      path: "/prices-ru",
      element: !applicationStore.isAuthenticated ?
        <Layout headerStyle={1} footerStyle={2}>
          <div style={{ margin: '50px auto', maxWidth: '1300px' }}>
            <Tariffs />
          </div>
        </Layout> : <Navigate to="/" replace />
    },
    {
      path: "/prices-uk",
      element: !applicationStore.isAuthenticated ?
        <Layout headerStyle={1} footerStyle={2}>
          <div style={{ margin: '50px auto', maxWidth: '1300px' }}>
            <Tariffs />
          </div>
        </Layout> : <Navigate to="/" replace />
    },
    { path: "/login", element: <Login /> },
    { path: "/:companyName/login", element: <Login /> },
    { path: "/register-company", element: <Register /> },
    { path: "/Account/ConfirmEmail", element: <ConfirmEmail /> },
    { path: "/Account/ConfirmChangedEmail", element: <ConfirmChangedEmail /> },
    { path: "/anonymous/project/tasks/:taskId", element: <AnonymousPage /> },
    { path: "/anonymous/project/tasks", element: <AnonymousPage /> },
    { path: "/anonymous/project", element: <AnonymousPage /> },


    // { 
    //   path: "/anonymous/project", 
    //   element: (
    //     <AnonymousPage>
    //       <ProjectContainerLayout breadcrumbTitle="Kanban Board" onProjectLoaded={() => setIsProjectLoaded(true)}>
    //             <KanbanTasks isShowInternalLoader={isProjectLoaded} />
    //       </ProjectContainerLayout>
    //     </AnonymousPage>
    //   ) 
    // },

    { 
      path: "/support", 
      element: applicationStore.isAuthenticated ? <TicketSupportPage /> : <SupportPage /> 
    },
    { 
      path: "/support/:publicId", 
      element: applicationStore.isAuthenticated ? 
      <TicketSupportPage /> : <SupportPage /> 
    },
    { 
      path: "/support-system", 
      element: applicationStore.isAuthenticated && applicationStore.company?.SupportSystem ? <SupportSystem /> : <Navigate to="/support" replace /> 
    },
    { 
      path: "/support-system/:publicId", 
      element: applicationStore.isAuthenticated && applicationStore.company?.SupportSystem ? <SupportSystem /> : <Navigate to="/support" replace /> 
    },
    {
      path: `/:tenant/`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <CompanyHome />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/settings`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated && applicationStore.user?.roles?.some(role => role.id === Roles.Owner || role.id === Roles.Admin)}>
          <CompanySettings />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/employees`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <Users />
        </RequireAuth>
      ),
    },
    {
      path: `/employees/delete`,
      element: (
        <DeleteAccount />
      ),
    },
    {
      path: `/:tenant/employees/resource`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <Resource />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/employees/:id`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <Account />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/payment-and-tariffs`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated && applicationStore.user?.roles?.some(role => role.id === Roles.Owner || role.id === Roles.Admin || role.id === Roles.Financier)}>
          <PaymentAndTariffs />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/api`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated && applicationStore.user?.roles?.some(role => role.id === Roles.Owner || role.id === Roles.Admin || role.id === Roles.Financier)}>
          <IntegrationAPI />
        </RequireAuth>
      ),
    },
    {
      path: `/api-list`,
      element: (
        <IntegrationAPI unauthorized={true} />
      ),
    },
    {
      path: `/:tenant/employees/:id/edit`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <EditEmployee />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/projects`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <ProjectsList />
        </RequireAuth>
      ),
    },

    {
      path: `/:tenant/erp/finances`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>

          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <CompanyFinances />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/erp/finances/:tabKey`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>

          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <CompanyFinances />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/crm`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <CompanyCRM />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },

    {
      path: `/:tenant/crm/contact/:contactId`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <ContactDetail />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/crm/calendar`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <CrmCalendar />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/crm/company/:companyId`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <ExternalCompanyDetail />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/crm/event/:eventId`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <EventDetail />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },


    {
      path: `/:tenant/crm/:tabKey`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <CompanyCRM />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/projects/:projectSlug`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <ProjectContainerLayout onProjectLoaded={() => setIsProjectLoaded(true)}>
            <ProjectPage
              isShowInternalLoader={isProjectLoaded}
              isAnonymous={false}
            />
          </ProjectContainerLayout>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/projects/:projectSlug/files`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <ProjectContainerLayout onProjectLoaded={() => setIsProjectLoaded(true)}>
              <ProjectFiles isShowInternalLoader={isProjectLoaded} />
            </ProjectContainerLayout>
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/projects/:projectSlug/tasks`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <ProjectContainerLayout breadcrumbTitle="Kanban Board" onProjectLoaded={() => setIsProjectLoaded(true)}>
              <KanbanTasks isShowInternalLoader={isProjectLoaded} />
            </ProjectContainerLayout>
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: "/:tenant/projects/:projectSlug/tasks/:taskId",
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <ProjectContainerLayout breadcrumbTitle="Kanban Board" onProjectLoaded={() => setIsProjectLoaded(true)}>
              <KanbanTasks isShowInternalLoader={isProjectLoaded} />
            </ProjectContainerLayout>
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },

    {
      path: `/:tenant/projects/:projectSlug/settings`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <ProjectSettings />
        </RequireAuth>
      ),
    },

    {
      path: `/:tenant/projects/:id/kanban`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <Kanban isEditMode={false} />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/kanban`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <Kanban isEditMode={false} />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/projects/:id/:documentId/edit`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <ProjectContainerLayout onProjectLoaded={() => setIsProjectLoaded(true)}>
            <ProjectPage
              isShowInternalLoader={isProjectLoaded}
              isAnonymous={false}
            />
          </ProjectContainerLayout>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/projects/:id/:documentId?`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <ProjectContainerLayout onProjectLoaded={() => setIsProjectLoaded(true)}>
            <ProjectPage
              isShowInternalLoader={isProjectLoaded}
              isAnonymous={false}
            />
          </ProjectContainerLayout>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/projects/:id/task/:taskId`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <TaskPage />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/tests`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <Tests />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/tests/passage/:id`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <PassageTest />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/tests/result/:id`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <UserAnswersTest />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/tests/create/:id?/:isEdit?`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <RequireCompanyTariff companyTariffPlan={applicationStore.company?.TariffPlanId ?? TariffPlan.No_Tariff} typePage={TypePage.Test}>
            <EditOrAddTest />
          </RequireCompanyTariff>
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/time-reports/:tabKey?`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <TimeReports />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/tests/:id`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <Tests />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/employees/salaries`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated && applicationStore.user?.roles?.some(role => [Roles.Financier, Roles.Owner].includes(role.id))}>
          <SalariesPage />
        </RequireAuth>
      ),
    },
    {
      path: `/:tenant/employees/organogram`,
      element: (
        <RequireAuth isAuthenticated={applicationStore.isAuthenticated}>
          <OrganogramPage />
        </RequireAuth>
      ),
    },

  ];
  if (!applicationStore.isLoading) {
    routes.push({
      path: "*",
      element: <Error404 />,
    });
  }
  if (applicationStore.isLoading) {
    return useRoutes([
      {
        path: `*`,
        element: <Preloader />,
      },
    ]);
  }
  return useRoutes(routes);
});

export default Router;
